import { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "./Avatar";
import { shouldPlaySound } from "./SoundToggle";

interface MatchFoundScreenProps {
  opponentUsername: string;
  wager: number;
  onComplete: () => void | Promise<void>;
}

function calculateLevel(wager: number): number {
  const level = Math.log2(wager / 0.01) + 1;
  return Math.round(level);
}

export default function MatchFoundScreen({
  opponentUsername,
  wager,
  onComplete,
}: MatchFoundScreenProps) {
  const [phase, setPhase] = useState<"match" | "ready" | "set" | "go">("match");
  const readySetGoAudioRef = useRef<HTMLAudioElement | null>(null);

  const level = useMemo(() => calculateLevel(wager), [wager]);

  // AUDIO
  useEffect(() => {
    const audio = new Audio("./ReadySetGo.mp3");
    audio.preload = "auto";
    readySetGoAudioRef.current = audio;

    return () => {
      if (readySetGoAudioRef.current) {
        readySetGoAudioRef.current.pause();
        readySetGoAudioRef.current = null;
      }
    };
  }, []);

  // READY–SET–GO TIMING
  useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase("ready");
      if (readySetGoAudioRef.current && shouldPlaySound()) {
        readySetGoAudioRef.current.play().catch(() => {});
      }
    }, 2900);

    const t2 = setTimeout(() => setPhase("set"), 3700);
    const t3 = setTimeout(() => setPhase("go"), 4500);
    const t4 = setTimeout(() => onComplete?.(), 5500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [onComplete]);

  const fade = {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.35 } },
    exit: { opacity: 0, scale: 1.01, transition: { duration: 0.25 } },
  } as const;

  // ------------ MOBILE / ORIENTATION --------------
  const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
  const initialMobile = typeof window !== 'undefined' ? Math.min(window.innerWidth, window.innerHeight) < 640 : false;
  const initialPortrait = typeof window !== 'undefined' ? window.innerHeight >= window.innerWidth : true;
  const [isMobile, setIsMobile] = useState(initialMobile);
  const [isPortrait, setIsPortrait] = useState(initialPortrait);
  const shouldCenter = isMobile && isCapacitor;

  useEffect(() => {
    if (!isCapacitor) {
      // On web, avoid re-centering/jumping on resize by keeping initial values
      return;
    }
    const checkViewport = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const minDim = Math.min(w, h);

      setIsMobile(minDim < 640);
      setIsPortrait(h >= w);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);
    window.addEventListener("orientationchange", checkViewport);

    return () => {
      window.removeEventListener("resize", checkViewport);
      window.removeEventListener("orientationchange", checkViewport);
    };
  }, [isCapacitor]);

  const isMobileLandscape = isMobile && !isPortrait;

  // ----------- RESPONSIVE SIZING ----------------------

  // Avatar
  const avatarSize = useMemo(() => {
    if (isMobile && isPortrait) return 80;  // perfect already
    if (isMobileLandscape) return 64;       // bigger (was 56)
    return 120;                             // desktop stays reduced
  }, [isMobile, isPortrait, isMobileLandscape]);

  // "Match found!"
  const matchHeadingClass = useMemo(() => {
    if (isMobile && isPortrait) return "text-3xl";
    if (isMobileLandscape) return "text-4xl"; // bigger (was 3xl)
    return "text-5xl";                        // desktop
  }, [isMobile, isPortrait, isMobileLandscape]);

  // Name
  const nameTextClass = useMemo(() => {
    if (isMobile && isPortrait) return "text-lg";
    if (isMobileLandscape) return "text-lg";  // bigger (was base)
    return "text-2xl";                        // desktop
  }, [isMobile, isPortrait, isMobileLandscape]);

  // Level
  const levelTextClass = useMemo(() => {
    if (isMobile && isPortrait) return "text-sm";
    if (isMobileLandscape) return "text-base"; // bigger (was sm)
    return "text-lg";                          // desktop
  }, [isMobile, isPortrait, isMobileLandscape]);

  // Portrait layout: avatar left
  const opponentRowLayoutClasses = useMemo(() => {
    if (isMobile && isPortrait) {
      return "flex-row items-center justify-center gap-3";
    }
    return "flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5";
  }, [isMobile, isPortrait]);

  const nameBoxAlignmentClasses = useMemo(() => {
    if (isMobile && isPortrait) return "text-left";
    return "text-center sm:text-left";
  }, [isMobile, isPortrait]);

  // -----------------------------------------------------

    return (
      <div
        className={`w-full h-full flex justify-center pointer-events-none px-3 ${
          shouldCenter ? "items-center" : "items-start"
        }`}
        style={{ transform: shouldCenter ? undefined : 'translateY(-24px)' }}
      >
        <div
          className="relative w-full pointer-events-auto"
          style={{
          maxWidth: isMobile
            ? isPortrait
              ? "95vw"
              : "100vw"
            : "min(600px, 60vw)",

          aspectRatio: isPortrait ? undefined : "16 / 9",

          maxHeight: isPortrait
            ? "80vh"
            : isMobileLandscape
            ? "70vh" // larger (was 65vh)
            : "70vh",

          minHeight: isPortrait ? "360px" : "auto",
          height: isPortrait ? "60vh" : undefined,
        }}
      >
        <img
          src="./Matched.png"
          alt="Wooden board background"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "contain" }}
          draggable={false}
        />

        {/* OVERLAY CONTENT */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={
            isMobileLandscape
              ? {
                  transform: "scale(0.95)", // bigger (was 0.9)
                  transformOrigin: "center center",
                }
              : undefined
          }
        >
          <div className="w-full max-w-[92%] mx-auto px-3 text-center">
            <AnimatePresence mode="wait">
              {phase === "match" ? (
                <motion.div
                  key="match"
                  {...fade}
                  className={`flex flex-col items-center ${
                    isPortrait ? "gap-3" : "gap-4"
                  } md:gap-6`}
                >
                  <div
                    className={`${matchHeadingClass} font-black tracking-wide text-[#3e2615] drop-shadow-[2px_2px_0_rgba(0,0,0,0.15)]`}
                  >
                    Match found!
                  </div>

                  <div className={`flex ${opponentRowLayoutClasses} w-full`}>
                    <Avatar
                      name={opponentUsername}
                      size={avatarSize}
                      className={`${
                        isPortrait ? "border-[2px]" : "border-[3px]"
                      } md:border-[5px] border-[#3e2615] shadow-md`}
                    />

                    <div
                      className={`${nameBoxAlignmentClasses} ${
                        isPortrait
                          ? "rounded-lg px-3 py-2"
                          : "rounded-xl md:rounded-2xl px-4 py-2"
                      } shadow-sm border-2`}
                      style={{
                        background: "rgba(245, 214, 164, 0.88)",
                        borderColor: "#7b4a2a",
                        backdropFilter: "blur(1px)",
                      }}
                    >
                      <div
                        className={`text-[#3e2615] font-extrabold leading-tight ${nameTextClass}`}
                      >
                        {opponentUsername}
                        {["Lump", "Ferris", "Magnus", "Sherlock"].includes(
                          opponentUsername
                        ) && " [AI]"}
                      </div>
                      <div
                        className={`text-[#3e2615] text-opacity-90 ${levelTextClass}`}
                      >
                        on level {level}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {phase === "ready" ? (
                      <motion.div
                        key="ready"
                        {...fade}
                        className="text-center w-full"
                      >
                        <div
                          className={`${
                            isPortrait
                              ? "text-3xl"
                              : isMobileLandscape
                              ? "text-4xl"
                              : "text-[clamp(22px,4vw,60px)]"
                          } md:text-[clamp(28px,5vw,64px)] font-black tracking-wide text-[#3e2615]`}
                        >
                          Get ready!
                        </div>
                      </motion.div>
                    ) : phase === "set" ? (
                      <motion.div
                        key="set"
                        {...fade}
                        className="text-center w-full"
                      >
                        <div
                          className={`${
                            isPortrait
                              ? "text-3xl"
                              : isMobileLandscape
                              ? "text-4xl"
                              : "text-[clamp(22px,4vw,60px)]"
                          } md:text-[clamp(28px,5vw,64px)] font-black tracking-wide text-[#3e2615]`}
                        >
                          Get set!
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="go"
                        {...fade}
                        className="text-center w-full"
                      >
                        <div
                          className={`${
                            isPortrait
                              ? "text-4xl"
                              : isMobileLandscape
                              ? "text-5xl"
                              : "text-[clamp(28px,5.5vw,80px)]"
                          } md:text-[clamp(34px,6.5vw,84px)] font-black tracking-wide text-[#3e2615]`}
                        >
                          Go!
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
